import { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiPlus, FiArrowLeft, FiTag } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { formatCurrency } from '../utils/currencyUtils';
import { Header, AddFeeModal, SkeletonTable } from '../components';
import { useStateContext } from '../contexts/ContextProvider';

const Addons = () => {
  const history = useHistory();
  const location = useLocation();
  const classId = location.pathname.split('/')[2];
  const className = decodeURIComponent(location.pathname.split('/')[3] || '');
  const { school } = useStateContext();
  const [addons, setAddons] = useState([]);
  const [addonName, setAddonName] = useState('');
  const [addonPrice, setAddonPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const schoolId = school?._id;

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  useEffect(() => {
    if (!schoolId) return;
    const fetchAddons = async () => {
      try {
        setLoading(true);
        const { data } = await userRequest.get(`class-addon/${schoolId}/${classId}`);
        setAddons((data.add_on || []).filter((a) => a?._id));
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddons();
  }, [classId, schoolId, refresh]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!addonName || !addonPrice) {
      setError('Please fill all the fields.');
      setTimeout(() => setError(''), 5000);
      return;
    }
    setLoading(true);
    try {
      await userRequest.post(`/class-addon/${schoolId}/${classId}`, {
        add_on_name: addonName,
        add_on_price: addonPrice,
      });
      setRefresh(!refresh);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setAddonName('');
        setAddonPrice('');
        toggleModal();
      }, 2000);
    } catch (error) {
      console.log(error);
      setError(error.response?.data || 'Failed to add fee. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const deleteHandler = async (addonId) => {
    try {
      setLoading(true);
      await userRequest.delete(`/class-addon/${schoolId}/${classId}/${addonId}`);
      setRefresh(!refresh);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">

      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => history.goBack()}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-indigo-600 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          type="button"
          onClick={toggleModal}
          className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
        >
          <FiPlus className="w-4 h-4" />
          Add Extra Fee
        </button>
      </div>

      <div className="mb-6">
        <Header category="Set Up" title={`${className} — Extra Fees`} />
        <p className="text-sm text-gray-400 -mt-2">Manage extra fees configured for this class.</p>
      </div>

      {loading ? (
        <SkeletonTable rows={4} columns={3} />
      ) : addons.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No extra fees configured yet.</div>
      ) : (
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Fee Name</th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {addons.map((addon) => (
                <tr key={addon._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-indigo-50 rounded-lg">
                        <FiTag className="text-indigo-500 w-3.5 h-3.5" />
                      </div>
                      <span className="font-medium text-gray-800 capitalize">{addon.add_on_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                      {formatCurrency(addon.add_on_price)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                      Active
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/update-addon/${classId}/${addon._id}`}>
                        <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                      </Link>
                      <button
                        onClick={() => deleteHandler(addon._id)}
                        className="p-2 rounded-full text-red-500 bg-red-50 hover:bg-red-100 transition-colors"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <AddFeeModal
          closeModal={toggleModal}
          addonName={addonName}
          setAddonName={setAddonName}
          addonPrice={addonPrice}
          setAddonPrice={setAddonPrice}
          error={error}
          success={success}
          loading={loading}
          submitHandler={submitHandler}
        />
      )}
    </div>
  );
};

export default Addons;
